import config from '@/config';
import { getZoomAppContext } from '@/integrations/zoom';
import { fetchAuthSession } from 'aws-amplify/auth';

/**
 * REST calls to the Zoom-specific API routes.
 *
 * The encrypted Zoom app context is forwarded verbatim; only the API holds the Zoom
 * client secret needed to decrypt and validate it. The browser never sends a raw
 * meeting UUID, and no Zoom identifier is persisted client-side.
 */

function zoomApiUrl(path: string): string {
  // config.domain.http points at `<api-origin>/graphql`; Zoom routes are siblings.
  return new URL(path, config.domain.http).toString();
}

async function authorizationHeader(): Promise<Record<string, string>> {
  try {
    const token = (await fetchAuthSession()).tokens?.accessToken?.toString();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

/**
 * Binds the Zoom meeting the host is currently in to a Scribl session so that other
 * participants who open the app land directly in the game. Requires an authenticated
 * host; the API re-verifies both the Cognito session and the Zoom host role.
 */
export async function bindZoomMeetingToSession(sessionId: string): Promise<boolean> {
  const appContext = await getZoomAppContext();
  if (!appContext) return false;

  // Never throws: `fetch` rejects outright when the API is unreachable (no network,
  // DNS failure, blocked by CORS), and binding is an enhancement rather than a
  // requirement. Callers must still be able to continue into the game.
  try {
    const response = await fetch(zoomApiUrl('/integrations/zoom/session/bind'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await authorizationHeader())
      },
      body: JSON.stringify({ appContext, sessionId })
    });

    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Resolves the Scribl session bound to the current Zoom meeting, if any.
 * Unauthenticated by design: meeting participants join as guests.
 */
export async function resolveZoomMeetingSession(): Promise<string | null> {
  const appContext = await getZoomAppContext();
  if (!appContext) return null;

  // Never throws, for the same reason as binding above. A null result simply means
  // "no game is bound to this meeting", which is also the correct answer when the
  // API is not running at all.
  try {
    const response = await fetch(zoomApiUrl('/integrations/zoom/session/resolve'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ appContext })
    });

    if (!response.ok) return null;

    const body = (await response.json()) as { sessionId?: string | null };
    return body.sessionId ?? null;
  } catch {
    return null;
  }
}
